import { getCells, setSelectedCell } from 'actions/orderActions';
import { connect } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { AppDispatch, RootState } from 'store/configureStore';
import { Cell, PointOfSale } from '@m4a3/api-client';
import { getAllMerchantDevicesForCell } from '../../actions/merchantDeviceActions';
import { MerchantDevice } from '@m4a3/api-client/dist/models/merchant-device.interface';
import { getPointOfSales } from '../../actions/adminActions';
import { PencilIcon, PlusSmallIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import SelectDropdown from '../../components/SelectDropdown/SelectDropdown';
import FilteredMultiSelectDropdown from '../../components/FilteredMultiSelectDropdown/FilteredMultiSelectDropdown';
import SecretText from '../../components/SecretText/SecretText';
import EmptyStateComponent from '../../components/EmptyState/EmptyState';
import LoadingBlock from '../../components/LoadingBlock/LoadingBlock';

interface MerchantDeviceListProps {
  cells: Cell[];
  selectedCellId: number,
  isLoadingMerchantDevices: boolean;
  merchantDevices: MerchantDevice[];
  merchantDevicesLoadingError: Error;
  pointOfSales: PointOfSale[];
  getCells: () => void;
  getAllMerchantDevicesForCell: (cellId: number) => void;
  getPointofSales: (cellId: number) => void;
  setSelectedCell: (cellId: number) => void;
}
function MerchantDeviceList ({
  cells,
  selectedCellId,
  isLoadingMerchantDevices,
  merchantDevices,
  merchantDevicesLoadingError,
  pointOfSales,
  getCells,
  getAllMerchantDevicesForCell,
  getPointofSales,
  setSelectedCell,
}: Readonly<MerchantDeviceListProps>) {
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [pointOfSaleIds, setPointOfSaleIds] = useState<number[]>([]);
  const [filteredMerchantDeviceList, setFilteredMerchantDeviceList] = useState<MerchantDevice[]>([]);

  useEffect(() => {
    getCells();
  }, []);

  useEffect(() => {
    setFilteredMerchantDeviceList(getFilteredMerchantDevices())
  }, [merchantDevices, statusFilter, pointOfSaleIds]);

  useEffect(() => {
    getAllMerchantDevicesForCell(selectedCellId);
    getPointofSales(selectedCellId);
  }, [selectedCellId]);

  

  const getFilteredMerchantDevices = (): MerchantDevice[] => {
    return merchantDevices?.filter((md) => {
      const statusMatch = statusFilter === 'ALL' || md.status === statusFilter;
      const pointOfSaleMatch = pointOfSaleIds.length === 0 || md.merchantIds.some(merchantId => pointOfSaleIds.includes(merchantId));

      return statusMatch && pointOfSaleMatch;
    })
  };

  const getMerchantNames = (merchantIds: number[]): string => {
    return merchantIds.map((merchantId) => {
      return pointOfSales.find((pos) => pos.id === merchantId)?.posName;
    })
    .filter(Boolean)
    .join(', ')
  };

  const getBodyContent = (): JSX.Element => {
    if (merchantDevices?.length === 0 && !isLoadingMerchantDevices) {
      return (<EmptyStateComponent entityName='Merchant Device' ctaUrl='configure' />);
    }
    return getMerchantDevicesTable(isLoadingMerchantDevices);
  }

  const getMerchantDevicesTable = (isLoading: boolean) => {
    return (<div className="px-4 sm:px-6 lg:px-8 mt-8 h-full flex flex-col">
      <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 pb-2">
          <table className="min-w-max divide-y divide-gray-300">
            <colgroup>
              <col className="w-32" />
              <col />
              <col className="w-40" />
              <col className="w-64" />
              <col className="w-64" />
              <col className="w-40" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-32">
                  Actions
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Device Id
                </th>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 w-40">
                  Username
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-64">
                  Description
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-64">
                  Merchants
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">
                  Additional filter
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMerchantDeviceList?.map((merchantDevice) => (
                <tr key={merchantDevice.id}>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-32">
                    {merchantDevice.status !== "DELETED" ?
                      <Link
                        to="configure"
                        className="inline-flex items-center gap-x-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        state={{merchantDevice: merchantDevice}}
                      >
                        <PencilIcon className="-ml-1.5 h-3 w-3" aria-hidden="true" />
                        Edit
                      </Link>
                    : '-'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {isLoading ? <LoadingBlock /> : merchantDevice.androidDeviceId || '-'}
                  </td>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 w-40">
                    {isLoading ? <LoadingBlock /> : merchantDevice.username}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    <div className="w-64 truncate">{isLoading ? <LoadingBlock /> : merchantDevice.description}</div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    <div className="w-64 truncate">{isLoading ? <LoadingBlock /> : getMerchantNames(merchantDevice.merchantIds)}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {isLoading ? <LoadingBlock /> : ((merchantDevice as any)?.additionalFilter ?? '-')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {isLoading ? <LoadingBlock /> : merchantDevice.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </div>);
  }

  return (
    <div className='bg-white min-h-screen h-full flex flex-col'>
      <header className="p-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-semibold leading-6 text-gray-900">Merchant Devices</h2>
            <p className="mt-2 text-sm text-gray-700">
              Login details for merchant devices.
            </p>
          </div>
          <div className="mt-4 flex md:ml-4 md:mt-0">
            <Link
              to="configure"
              className="ml-auto flex items-center gap-x-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              <PlusSmallIcon className="-ml-1.5 h-5 w-5" aria-hidden="true" />
              New device
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-4">
          <div className='col-span-1 rounded-lg'>
            <SelectDropdown
              options={cells?.map((c) => {
                return {
                  displayText: c.displayName,
                  value: c.id,
                };
              })}
              value={selectedCellId}
              onChange={(newVal: number) => setSelectedCell(newVal)}
              label='Cell' />
          </div>
          <div className='col-span-1 rounded-lg'>
            <SelectDropdown options={[{value: 'ALL', displayText: 'All'}, {value: 'ACTIVE', displayText: 'Active'}, {value: 'DELETED', displayText: 'Deleted'}]} value={statusFilter} onChange={(newVal: string) => setStatusFilter(newVal)} label='Status' />
          </div>
          <div className='col-span-1 rounded-lg'>
            <FilteredMultiSelectDropdown
                label="Point of sales"
                value={pointOfSaleIds}
                onChange={(pointOfSaleIds: PointOfSale['id'][]) => {
                  setPointOfSaleIds(pointOfSaleIds)
                }}
                options={pointOfSales?.map((pointOfSale: PointOfSale) => ({
                  value: pointOfSale.id,
                  displayText: pointOfSale.posName,
                }))} />
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0">
        {getBodyContent()}
      </div>
    </div>
  );
}
const mapStateToProps = (state: RootState) => ({
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  isLoadingMerchantDevices: state.merchantDevices.isLoadingMerchantDevices,
  merchantDevices: state.merchantDevices.merchantDevices,
  merchantDevicesLoadingError: state.merchantDevices.merchantDevicesLoadingError,
  pointOfSales: state.admin.pointOfSales,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getCells: () => getCells()(dispatch),
  getAllMerchantDevicesForCell: (cellID: number) => getAllMerchantDevicesForCell(cellID)(dispatch),
  getPointofSales: (cellId: number) => getPointOfSales(cellId)(dispatch),
  setSelectedCell: (cellId: number) => setSelectedCell(cellId)(dispatch),
});
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MerchantDeviceList);
